import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const TutorialOverlay = ({
    isVisible,
    onClose,
    title,
    description,
    position = "center",
    targetRef,
    onNext,
    isLastStep
}) => {
    const [highlightStyle, setHighlightStyle] = useState({});
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0
    });
    const overlayRef = useRef(null);
    const { t } = useTranslation();

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (targetRef?.current) {
            const updatePosition = () => {
                const rect = targetRef.current.getBoundingClientRect();
                setHighlightStyle({
                    position: 'fixed',
                    top: `${rect.top}px`,
                    left: `${rect.left}px`,
                    width: `${rect.width}px`,
                    height: `${rect.height}px`,
                });
            };

            updatePosition();
            window.addEventListener('scroll', updatePosition);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', updatePosition);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [targetRef, position]);

    const isMobile = windowSize.width < 768;

    const getPositionStyles = () => {
        if (isMobile) {
            if (position === 'next-button') {
                return 'bottom-20 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md';
            }
            return 'bottom-24 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-md';
        }

        switch (position) {
            case 'top-form':
            case 'table':
            case 'form-parameters':
            case 'top-right':
                return 'top-24 right-8';
            case 'next-button':
                return 'bottom-32 right-32';
            default:
                return 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';
        }
    };

    useEffect(() => {
        if (overlayRef.current && isVisible) {
            const rect = overlayRef.current.getBoundingClientRect();
            
            if (rect.bottom > window.innerHeight) {
                overlayRef.current.style.top = 'auto';
                overlayRef.current.style.bottom = '20px';
                overlayRef.current.style.maxHeight = '80vh';
                overlayRef.current.style.overflowY = 'auto';
            }
            
            if (rect.right > window.innerWidth) {
                overlayRef.current.style.right = '20px';
                overlayRef.current.style.left = 'auto';
                overlayRef.current.style.transform = 'none';
            }
        }
    }, [isVisible, windowSize]);

    return (
        <AnimatePresence>
            {isVisible && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 bg-black z-40'
                    />

                    {targetRef?.current && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ 
                                opacity: 1,
                                borderColor: ['rgba(79, 70, 229, 0.3)', 'rgba(79, 70, 229, 0.8)', 'rgba(79, 70, 229, 0.3)']
                            }}
                            exit={{ opacity: 0 }}
                            transition={{
                                borderColor: {
                                    duration: 2,
                                    repeat: Infinity,
                                }
                            }}
                            style={highlightStyle}
                            className="border-4 rounded-lg z-40 pointer-events-none"
                        />
                    )}

                    <motion.div
                        ref={overlayRef}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className={`fixed ${getPositionStyles()} z-50 bg-white rounded-lg shadow-xl p-4 md:p-6 max-w-md`}
                    >
                        <button
                            onClick={onClose}
                            className='absolute top-2 right-2 text-gray-400 hover:text-gray-600'
                        >
                            <X size={20} />
                        </button>

                        <h3 className='font-semibold text-xl text-gray-900 mb-2'>{title}</h3>
                        <p className='text-gray-600 mb-6'>{description}</p>

                        <div className='flex justify-end'>
                            {onNext && (
                                <button
                                    onClick={onNext}
                                    className='flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors'
                                >
                                    {isLastStep ? t("tutorial.button.finish") : t("tutorial.button.next")}
                                    <ArrowRight size={16} />
                                </button>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default TutorialOverlay;